import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EnergyPlusSVG from 'assets/partners/EnergyPlus.svg';
import { useSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent, DragEvent, MouseEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  loadMeta,
  PUBLIC_ANALYZE_MAX_IDF_BYTES,
  saveIdf,
  saveMeta,
} from 'utils/publicAnalyzeDraft';
import {
  applyRenames,
  buildDupRenameMap,
  collectXAround,
  conditionedDups,
  detectKind,
  fixedName,
  highlightParts,
  outputFileName,
  parseIdfZones,
  parseJsonZones,
  renameZones,
  type ModelKind,
  type NamingMode,
  type ParsedZone,
  type XAroundItem,
} from 'utils/zoneNames';
import './ZoneNamesPage.css';

const WHATSAPP_PHONE = '972544780198';

const sanitizeModelError = (msg: string) =>
  msg.replace(/epjson/gi, 'model').replace(/\bjson\b/gi, 'model');

const NameText = ({ name, needles }: { name: string; needles: string[] }) => (
  <>
    {highlightParts(name, needles).map((part, index) =>
      part.highlight ? (
        <span key={`${part.text}-${index}`} className="zone-names__dup-num">
          {part.text}
        </span>
      ) : (
        <span key={`${part.text}-${index}`}>{part.text}</span>
      )
    )}
  </>
);

const ZoneNamesPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const inputRef = useRef<HTMLInputElement>(null);
  const navTimer = useRef<number | null>(null);

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [mode, setMode] = useState<NamingMode>('floor');
  const [fileName, setFileName] = useState('');
  const [fileText, setFileText] = useState('');
  const [kind, setKind] = useState<ModelKind | ''>('');
  const [zones, setZones] = useState<ParsedZone[]>([]);
  const [pendingX, setPendingX] = useState<XAroundItem[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [status, setStatus] = useState<{ msg: string; tone: '' | 'ok' | 'err' }>({
    msg: '',
    tone: '',
  });

  useEffect(() => {
    const previousTitle = document.title;
    document.title = t('ZONE_NAMES_TITLE');
    return () => {
      document.title = previousTitle;
    };
  }, [i18n.language, t]);

  useEffect(
    () => () => {
      if (navTimer.current) window.clearTimeout(navTimer.current);
    },
    []
  );

  const clearAll = () => {
    setFileName('');
    setFileText('');
    setKind('');
    setZones([]);
    setPendingX([]);
    if (inputRef.current) inputRef.current.value = '';
    setStep(1);
  };

  const publishFixed = async (
    text: string,
    sourceName: string,
    modelKind: ModelKind,
    message: string
  ) => {
    const outName = outputFileName(sourceName, modelKind);
    const blob = new Blob([text], {
      type: modelKind === 'json' ? 'application/json;charset=utf-8' : 'text/plain;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = outName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    setStep(1);
    setStatus({ msg: message, tone: 'ok' });

    if (modelKind !== 'idf') return;
    if (blob.size > PUBLIC_ANALYZE_MAX_IDF_BYTES) {
      enqueueSnackbar(t('PUBLIC_ANALYZE_IDF_TOO_LARGE'), { variant: 'error' });
      return;
    }
    try {
      const existing = loadMeta();
      saveMeta({
        type: existing?.type || 'Residential',
        climate: existing?.climate || '',
        terrain: existing?.terrain || 'city',
        single_block_per_floor: mode === 'floor',
        skip_overwrite: existing?.skip_overwrite ?? true,
        pendingAction: null,
        version: '4.x.x',
        idfFileName: outName,
        analyzeSummary: null,
      });
      await saveIdf(new File([blob], outName, { type: 'application/octet-stream' }));
    } catch {
      enqueueSnackbar(t('PUBLIC_ANALYZE_DRAFT_SAVE_FAILED'), { variant: 'error' });
      return;
    }
    navTimer.current = window.setTimeout(() => navigate('/try'), 400);
  };

  const loadFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result ?? '');
        const detected = detectKind(file.name, text);
        const parsed = detected === 'json' ? parseJsonZones(text) : parseIdfZones(text);
        if (!parsed.length) throw new Error('No Zone objects found');
        setFileText(text);
        setKind(detected);
        setZones(parsed);
        setPendingX([]);
        setFileName(file.name);
        setStatus({ msg: '', tone: '' });
      } catch (error) {
        clearAll();
        const raw = error instanceof Error ? error.message : String(error);
        setStatus({
          msg: t('ZONE_NAMES_INVALID_FILE', { msg: sanitizeModelError(raw) }),
          tone: 'err',
        });
      }
    };
    reader.readAsText(file);
  };

  const onCheck = () => {
    if (!zones.length) return;
    const items = collectXAround(zones, mode);
    setStatus({ msg: '', tone: '' });
    if (!items.length) {
      setPendingX([]);
      setStep(3);
      return;
    }
    setPendingX(items);
    setStep(2);
  };

  const onApply = () => {
    if (!kind) return;
    const map: Record<string, string> = {};
    pendingX.forEach((item) => {
      if (item.fixed && item.fixed !== item.name) map[item.name] = item.fixed;
    });
    const renamed = Object.keys(map).length;
    const nextText = renamed ? applyRenames(fileText, kind, map) : fileText;
    const nextZones = renamed ? renameZones(zones, map) : zones;
    if (renamed) {
      setFileText(nextText);
      setZones(nextZones);
    }
    setPendingX([]);
    setStep(3);
    if (renamed && !conditionedDups(nextZones).length) {
      const outName = outputFileName(fileName, kind);
      void publishFixed(
        nextText,
        fileName,
        kind,
        `${t('ZONE_NAMES_DOWNLOADED', { name: outName })} (${t('ZONE_NAMES_SPECIAL_FIX_RENAMES', { n: renamed })})`
      );
      return;
    }
    if (renamed) {
      setStatus({ msg: t('ZONE_NAMES_APPLIED_SPECIAL', { n: renamed }), tone: 'ok' });
    }
  };

  const onDownload = () => {
    if (!kind) return;
    const map = buildDupRenameMap(zones, mode);
    const count = Object.keys(map).length;
    if (!count) {
      setStatus({ msg: t('ZONE_NAMES_NOTHING'), tone: 'err' });
      setStep(1);
      return;
    }
    void publishFixed(
      applyRenames(fileText, kind, map),
      fileName,
      kind,
      t('ZONE_NAMES_DOWNLOADED_OPEN', {
        name: outputFileName(fileName, kind),
        count,
      })
    );
  };

  const onBackFromDuplicates = () => {
    const items = collectXAround(zones, mode);
    if (items.length) {
      setPendingX(items);
      setStep(2);
      return;
    }
    setStep(1);
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    const file = event.dataTransfer.files?.[0];
    if (file) loadFile(file);
  };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) loadFile(file);
  };

  const onClear = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    clearAll();
    setStatus({ msg: '', tone: '' });
  };

  const dups = conditionedDups(zones);
  const conditioned = zones.filter((zone) => zone.partOfTotal).length;
  const extraCount = pendingX.filter((item) => item.kind === 'extra').length;
  const missingCount = pendingX.filter((item) => item.kind === 'missing').length;

  return (
    <div className="zone-names">
      <div className="zone-names__topbar">
        <h1 className="zone-names__title">{t('ZONE_NAMES_TITLE')}</h1>
      </div>

      {step === 1 && (
        <section className={`zone-names__panel${fileName ? ' has-file' : ''}`}>
          <div className="zone-names__step1-head">
            <div className="zone-names__step1-text">
              <h2 className="zone-names__panel-title">{t('ZONE_NAMES_UPLOAD')}</h2>
              <p className="zone-names__panel-sub">{t('ZONE_NAMES_UPLOAD_SUB')}</p>
            </div>
            <label className="zone-names__naming">
              <span className="zone-names__field-label">{t('ZONE_NAMES_NAMES')}</span>
              <select
                className="zone-names__select"
                aria-label={t('ZONE_NAMES_NAMING_ARIA')}
                value={mode}
                onChange={(event) => setMode(event.target.value === 'unit' ? 'unit' : 'floor')}
              >
                <option value="floor">{t('ZONE_NAMES_FLOOR')}</option>
                <option value="unit">{t('ZONE_NAMES_UNIT')}</option>
              </select>
            </label>
          </div>

          {status.msg ? (
            <p className={`zone-names__status${status.tone ? ` is-${status.tone}` : ''}`}>{status.msg}</p>
          ) : null}

          <div
            className={`zone-names__dropzone${dragOver ? ' is-drag' : ''}`}
            role="button"
            tabIndex={0}
            aria-label={t('ZONE_NAMES_UPLOAD_ARIA')}
            onDragEnter={(event) => {
              event.preventDefault();
              setDragOver(true);
            }}
            onDragOver={(event) => {
              event.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={(event) => {
              event.preventDefault();
              setDragOver(false);
            }}
            onDrop={onDrop}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                inputRef.current?.click();
              }
            }}
          >
            <img className="zone-names__ep" src={EnergyPlusSVG} alt="EnergyPlus" width={126} height={88} />
            <p className="zone-names__drop-title">{t('ZONE_NAMES_DROP_TITLE')}</p>
            <div className="zone-names__file" aria-hidden="true">
              <svg className="zone-names__file-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round">
                <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                <polyline points="13 2 13 9 20 9" />
              </svg>
              <p className="zone-names__filename">{fileName}</p>
            </div>
            <input
              ref={inputRef}
              className="zone-names__input"
              type="file"
              accept=".idf,.IDF,.json,.epjson,.JSON,.epJSON,application/json,text/plain"
              onChange={onFileChange}
            />
            <button
              type="button"
              className="zone-names__clear"
              title={t('ZONE_NAMES_CLEAR')}
              aria-label={t('ZONE_NAMES_CLEAR')}
              onClick={onClear}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <button type="button" className="zone-names__check" disabled={!zones.length} onClick={onCheck}>
            {t('ZONE_NAMES_CHECK')}
          </button>
        </section>
      )}

      {step === 2 && (
        <section className="zone-names__panel">
          <h2 className="zone-names__panel-title">{t('ZONE_NAMES_SPECIAL_FIX')}</h2>
          <p className="zone-names__panel-sub">
            <Trans i18nKey="ZONE_NAMES_SPECIAL_FIX_SUB" components={{ code: <code /> }} />
          </p>
          <div className="zone-names__summary">
            <span className="zone-names__pill">Special Fix {pendingX.length}</span>
            {extraCount > 0 ? <span className="zone-names__pill is-warn">Extra {extraCount}</span> : null}
            {missingCount > 0 ? <span className="zone-names__pill is-info">Missing {missingCount}</span> : null}
          </div>
          <div className="zone-names__table-wrap">
            <table className="zone-names__table">
              <thead>
                <tr>
                  <th>Zone name</th>
                  <th>Issue</th>
                  <th>After</th>
                </tr>
              </thead>
              <tbody>
                {pendingX.map((item) => {
                  const issue =
                    item.kind === 'missing'
                      ? 'Missing X'
                      : `Extra X${item.removeSegs.length ? ` (${item.removeSegs.join(', ')})` : ''}`;
                  return (
                    <tr key={item.name} className={item.kind === 'missing' ? 'is-missing' : 'is-extra'}>
                      <td className="zone-names__name">
                        <NameText
                          name={item.name}
                          needles={item.kind === 'extra' ? item.removeSegs : []}
                        />
                      </td>
                      <td>
                        <span className={`zone-names__tag is-${item.kind}`}>{issue}</span>
                      </td>
                      <td className="zone-names__name zone-names__new">{item.fixed}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="zone-names__row">
            <button type="button" className="zone-names__ghost" onClick={() => setStep(1)}>
              {t('ZONE_NAMES_BACK')}
            </button>
            <button type="button" className="zone-names__primary" disabled={!pendingX.length} onClick={onApply}>
              {t('ZONE_NAMES_APPLY')}
            </button>
          </div>
        </section>
      )}

      {step === 3 && (
        <section className="zone-names__panel">
          <h2 className="zone-names__panel-title">{t('ZONE_NAMES_DUP_TITLE')}</h2>
          {status.msg ? (
            <p className={`zone-names__status${status.tone ? ` is-${status.tone}` : ''}`}>{status.msg}</p>
          ) : null}
          <div className="zone-names__summary">
            <span className="zone-names__pill">Zones {zones.length}</span>
            <span className="zone-names__pill is-ok">Conditioned {conditioned}</span>
            <span className={`zone-names__pill${dups.length ? ' is-warn' : ' is-ok'}`}>
              Duplicates {dups.length}
            </span>
            <span className="zone-names__pill">{mode === 'unit' ? 'Unit Block' : 'Floor Block'}</span>
          </div>
          {!dups.length ? (
            <div className="zone-names__empty">{t('ZONE_NAMES_NO_DUPS')}</div>
          ) : (
            <div className="zone-names__table-wrap">
              <table className="zone-names__table">
                <thead>
                  <tr>
                    <th>Before</th>
                    <th>After</th>
                  </tr>
                </thead>
                <tbody>
                  {dups.map((row) => (
                    <tr key={row.name} className="is-dup">
                      <td className="zone-names__name">
                        <NameText name={row.name} needles={[row.unit]} />
                      </td>
                      <td className="zone-names__name zone-names__new">{fixedName(row.parsed, mode)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="zone-names__row">
            <button type="button" className="zone-names__ghost" onClick={onBackFromDuplicates}>
              {t('ZONE_NAMES_BACK')}
            </button>
            <button type="button" className="zone-names__primary" disabled={!dups.length} onClick={onDownload}>
              {t('ZONE_NAMES_DOWNLOAD')}
            </button>
          </div>
        </section>
      )}

      <div className="zone-names__support">
        <p>{t('ZONE_NAMES_SUPPORT')}</p>
        <a
          className="zone-names__wa"
          href={`https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(t('ZONE_NAMES_WHATSAPP'))}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="WhatsApp +972 54-478-0198"
        >
          <WhatsAppIcon sx={{ fontSize: 32 }} />
        </a>
      </div>
    </div>
  );
};

export default ZoneNamesPage;
